<script setup lang="ts">
import { faIconStyle } from '~/utils/faIconStyle'
const { getPedagogyFeatures } = useSchool()
const { data: features } = await useAsyncData('school-pedagogy', () => getPedagogyFeatures())

// Only one card can be flipped at a time
const flippedIndex = ref<number | null>(null)
const toggleFlip = (index: number) => {
  flippedIndex.value = flippedIndex.value === index ? null : index
}
const isFlipped = (index: number) => flippedIndex.value === index

const { elementRef: sectionRef, isVisible: isVisible } = useScrollReveal()
</script>

<template>
  <section id="pedagogie" ref="sectionRef" class="py-section-py bg-background text-foreground relative border-t border-oxo-border transition-colors duration-300">
    <div class="container mx-auto px-container-px relative z-10">
      <div class="mb-section-header-mb transition-all duration-1000 mx-auto text-center" :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'">
        <UiSectionHeader 
          titlePrefix="Notre"
          titleHighlight="Pédagogie"
          subtitle="Une approche immersive et concrète, calquée sur les réalités des studios de développement."
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        <div 
          v-for="(feature, index) in features" 
          :key="index"
          class="relative group h-[380px] perspective-1000 cursor-pointer w-[350px] max-w-full flex-none mx-auto"
          :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'"
          :style="{ transitionDelay: (300 + (index * 150)) + 'ms' }"
          @click="toggleFlip(index)"
        >
          <!-- Flip Container -->
          <div 
            class="relative w-full h-full transition-transform duration-700 preserve-3d"
            :class="{ 'rotate-y-180': isFlipped(index) }"
          >
            <!-- Front Side -->
            <div class="absolute inset-0 backface-hidden" style="transform: rotateX(0deg);">
              <div class="h-full bg-oxo-card-bg p-10 rounded-3xl border border-oxo-border shadow-lg transition-all duration-500 group-hover:-translate-y-2 flex flex-col items-center justify-center">
                <!-- Icon Box -->
                <div class="w-16 h-16 rounded-2xl bg-oxo-red/10 dark:bg-white/5 shadow-xs flex items-center justify-center text-oxo-red text-2xl mb-8 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                  <i :style="faIconStyle('fas fa-' + feature.icon.replace('fa-', '').replace('fas ', ''))" :class="'fas fa-' + feature.icon.replace('fa-', '').replace('fas ', '')" aria-hidden="true"></i>
                </div>

                <!-- Content -->
                <h3 class="font-display font-bold text-2xl text-foreground mb-4 text-center">
                  {{ feature.title }}
                </h3>

                <div class="mt-6 text-oxo-red/30 group-hover:text-oxo-red transition-colors duration-300">
                  <i class="fas fa-info-circle animate-pulse" aria-hidden="true"></i>
                </div>
              </div>
            </div>

            <!-- Back Side -->
            <div class="absolute inset-0 backface-hidden rotate-y-180">
              <div class="h-full bg-linear-to-br from-oxo-red to-oxo-red/80 p-10 rounded-3xl flex flex-col items-center justify-center text-center text-white shadow-2xl">
                <div class="mb-6 opacity-40">
                  <i :style="faIconStyle('fas fa-' + feature.icon.replace('fa-', '').replace('fas ', ''))" :class="'fas fa-' + feature.icon.replace('fa-', '').replace('fas ', '') + ' text-5xl'" aria-hidden="true"></i>
                </div>
                <h4 class="font-display font-black text-xl mb-4 uppercase">
                  {{ feature.title }}
                </h4>
                <p class="text-sm font-medium leading-relaxed">
                  {{ feature.description }}
                </p>
                <div class="absolute bottom-8 text-white/50 text-xs uppercase tracking-widest font-black">
                   Cliquer pour fermer
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.perspective-1000 {
  perspective: 1000px;
}
.preserve-3d {
  transform-style: preserve-3d;
}
.backface-hidden {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  /* Firefox fix: force GPU layer */
  transform: translateZ(0);
}
.rotate-y-180 {
  transform: rotateY(180deg);
}

/* Firefox-specific fix for front face when flipped */
.rotate-y-180 .backface-hidden:first-child {
  visibility: hidden;
}
.rotate-y-180 .backface-hidden:last-child {
  visibility: visible;
}
</style>
